import { SideModal } from "shared";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import "./InvestmentPolicyDetailsSideModal.scss";

interface InvestmentPolicyDetailsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

const PAYMENT_LABELS: Record<string, string> = {
  monthly: "Miesięczne",
  quarterly: "Kwartalne",
  yearly: "Roczne",
  at_end_of_year: "Na koniec roku",
  at_end: "Na koniec roku",
};

const InvestmentPolicyDetailsSideModal = ({
  isOpen,
  onClose,
  item,
}: InvestmentPolicyDetailsSideModalProps) => {
  const formatDate = (dateString: any): string => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("pl-PL");
  };

  const getDurationText = (): string => {
    if (!item) return "—";
    const durationDate = item.details?.durationDate;
    if (durationDate === null || durationDate === undefined) {
      return "Bezterminowa";
    }
    if (typeof durationDate === "string" && durationDate !== "") {
      const d = new Date(durationDate);
      if (isNaN(d.getTime())) return "—";
      // Check if it's the default "indefinite" date
      if (d.getFullYear() === 2000 && d.getMonth() === 0 && d.getDate() === 1) {
        return "Bezterminowa";
      }
      return d.toLocaleDateString("pl-PL");
    }
    return "—";
  };

  const getPremiumPaymentText = (): string => {
    if (!item) return "—";
    const payment = item.details?.premiumPayment;
    if (!payment) return "—";
    return PAYMENT_LABELS[payment] || payment;
  };

  const contractFiles = (item?.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const handleDownloadAll = () => {
    if (!item) return;
    contractFiles.forEach((file: any) => {
      const link = document.createElement("a");
      link.href = file.file.url;
      link.download = file.file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Szczegółowe informacje"
      subtitle="Aktywo: Polisy inwestycyjne / IKE"
    >
      <div className="investment-policy-details">
        <div className="investment-policy-details__field">
          <span className="investment-policy-details__label">Nazwa</span>
          <span className="investment-policy-details__value">
            {item?.name || "—"}
          </span>
        </div>

        <div className="investment-policy-details__field">
          <span className="investment-policy-details__label">Waluta</span>
          <span className="investment-policy-details__value">
            {item?.currency?.currency || "—"}
          </span>
        </div>

        <div className="investment-policy-details__field">
          <span className="investment-policy-details__label">
            Data zawarcia
          </span>
          <span className="investment-policy-details__value">
            {formatDate(item?.details?.contractDate)}
          </span>
        </div>

        <div className="investment-policy-details__field">
          <span className="investment-policy-details__label">Czas trwania</span>
          <span className="investment-policy-details__value">
            {getDurationText()}
          </span>
        </div>

        <div className="investment-policy-details__field">
          <span className="investment-policy-details__label">Składki</span>
          <span className="investment-policy-details__value">
            {getPremiumPaymentText()}
          </span>
        </div>

        <div className="investment-policy-details__field">
          <span className="investment-policy-details__label">
            Dodatkowe informacje
          </span>
          <span className="investment-policy-details__value">
            {item?.additionalInfo || "—"}
          </span>
        </div>

        <div className="investment-policy-details__field investment-policy-details__field--files">
          <div className="investment-policy-details__files-header">
            <span className="investment-policy-details__label">Umowa</span>
            {contractFiles.length > 0 && (
              <button
                className="investment-policy-details__download-all"
                onClick={handleDownloadAll}
              >
                Pobierz wszystko
                <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
              </button>
            )}
          </div>
          {contractFiles.length === 0 ? (
            <span className="investment-policy-details__value">—</span>
          ) : (
            <div className="investment-policy-details__files-list">
              {contractFiles.map((file: any) => (
                <div
                  key={file.file.uuid}
                  className="investment-policy-details__file-item"
                >
                  <span className="investment-policy-details__file-name">
                    {file.file.name}
                  </span>
                  <a
                    href={file.file.url}
                    download={file.file.name}
                    target="_blank"
                    rel="noreferrer"
                    className="investment-policy-details__file-download"
                  >
                    <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default InvestmentPolicyDetailsSideModal;
